import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, ChevronsUpDown, Plus, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useDivision } from '@/context/DivisionContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContractorCombobox = ({ 
  value, 
  onSelect, 
  onAddNew, 
  authHeaders,
  placeholder = "Select contractor..." 
}) => {
  const { division } = useDivision();
  const [open, setOpen] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (open) {
      fetchContractors();
    }
  }, [open, division]);

  const fetchContractors = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contractors`, {
        headers: authHeaders,
        params: { division }
      });
      setContractors(response.data);
    } catch (error) {
      console.error('Error fetching contractors:', error);
    }
    setLoading(false);
  };

  const selectedContractor = contractors.find(c => c.name === value);

  // Filter contractors based on search term
  const filteredContractors = contractors.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.contact_person && c.contact_person.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (c.city && c.city.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-[#1e293b] border-[rgba(255,255,255,0.1)] text-white hover:bg-[#334155] hover:border-[#f97316] h-12"
        >
          {value ? (
            <div className="flex items-center gap-2 truncate">
              <User className="w-4 h-4 text-[#f97316]" />
              <span className="uppercase font-semibold">{value}</span>
            </div>
          ) : (
            <span className="text-[#94a3b8]">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0 bg-[#1e293b] border-[rgba(255,255,255,0.2)]" align="start">
        <Command className="bg-[#1e293b]">
          <CommandInput 
            placeholder="Search contractors..." 
            className="text-white"
            value={searchTerm}
            onValueChange={setSearchTerm}
          />
          <CommandList>
            <CommandEmpty className="text-[#94a3b8]">
              {loading ? 'Loading contractors...' : 'No contractors found.'}
            </CommandEmpty>
            
            {/* Add New Contractor Option */}
            <CommandGroup heading="Actions">
              <CommandItem
                onSelect={() => {
                  setOpen(false);
                  onAddNew();
                }}
                className="text-[#10b981] cursor-pointer hover:bg-[#334155]"
              >
                <Plus className="mr-2 h-4 w-4" />
                <span className="font-semibold">Add New Contractor</span>
              </CommandItem>
            </CommandGroup>

            <CommandSeparator className="bg-[rgba(255,255,255,0.1)]" />

            {/* Existing Contractors */}
            {filteredContractors.length > 0 && (
              <CommandGroup heading="Existing Contractors">
                {filteredContractors.map((contractor) => (
                  <CommandItem
                    key={contractor.id}
                    value={contractor.name}
                    onSelect={() => {
                      onSelect(contractor.name);
                      setOpen(false);
                      setSearchTerm('');
                    }}
                    className="cursor-pointer hover:bg-[#334155] text-white"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === contractor.name ? "opacity-100 text-[#f97316]" : "opacity-0"
                      )}
                    />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="font-semibold uppercase">{contractor.name}</span>
                      {(contractor.contact_person || contractor.city) && (
                        <span className="text-xs text-[#94a3b8] truncate">
                          {contractor.contact_person && `${contractor.contact_person}`}
                          {contractor.contact_person && contractor.city && ' • '}
                          {contractor.city && `${contractor.city}, ${contractor.state || ''}`}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default ContractorCombobox;
